import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import { AlertTriangle, Building2, Clock, MailCheck } from 'lucide-react'
import { toast } from 'sonner'
import { notifyError } from '@/components/notify'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { errorMessage, isApiError } from '@/lib/api'
import { formatDate } from '@/lib/format'
import { invitationPreviewOptions, meQueryOptions, useAcceptInvitation, useLogout, useMe, useSwitchTenant } from '../api'
import { AuthLayout } from '../components/auth-layout'

const route = getRouteApi('/invite/$token')

export function InvitePage() {
  const { token } = route.useParams()
  const preview = useQuery(invitationPreviewOptions(token))
  const me = useMe()
  const accept = useAcceptInvitation(token)
  const logout = useLogout()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const switchTenant = useSwitchTenant()
  const redirect = `/invite/${token}`

  if (preview.isPending || me.isPending) {
    return (
      <AuthLayout title="You’re invited" description="Loading invitation…">
        <div className="space-y-3">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-11 w-full" />
        </div>
      </AuthLayout>
    )
  }

  if (preview.isError) {
    const expired = isApiError(preview.error) && (preview.error.status === 404 || preview.error.status === 410)
    return (
      <AuthLayout title={expired ? 'Invitation unavailable' : 'Something went wrong'} description={expired ? 'This invitation has expired, been revoked or was already used.' : errorMessage(preview.error)}>
        <div className="flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />
          Ask the person who invited you to send a new invitation.
        </div>
        <Button asChild className="mt-6 w-full" variant="outline">
          <Link to="/login">Go to sign in</Link>
        </Button>
      </AuthLayout>
    )
  }

  const invite = preview.data
  const user = me.data?.user
  const emailMismatch = !!user && user.email.toLowerCase() !== invite.email.toLowerCase()

  return (
    <AuthLayout title={`Join ${invite.tenant_name}`} description="You’ve been invited to collaborate on Signetix.">
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="flex items-center gap-3 border-b bg-muted/40 p-4">
          <div className="flex size-10 items-center justify-center rounded-lg bg-brand-gradient text-white">
            <Building2 className="size-5" />
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold">{invite.tenant_name}</p>
            <p className="text-xs text-muted-foreground">Organization</p>
          </div>
          <Badge className="ml-auto">{invite.role_name}</Badge>
        </div>
        <dl className="grid gap-2 p-4 text-sm">
          <div className="flex items-center gap-2">
            <MailCheck className="size-4 text-muted-foreground" />
            <dt className="text-muted-foreground">Invited email</dt>
            <dd className="ml-auto truncate font-medium">{invite.email}</dd>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="size-4 text-muted-foreground" />
            <dt className="text-muted-foreground">Expires</dt>
            <dd className="ml-auto font-medium">{formatDate(invite.expires_at)}</dd>
          </div>
        </dl>
      </div>

      <div className="mt-6 grid gap-3">
        {!user ? (
          <>
            <Button asChild size="lg" variant="brand">
              <Link to="/register" search={{ redirect, email: invite.email }}>
                Create account &amp; join
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/login" search={{ redirect }}>
                I already have an account
              </Link>
            </Button>
          </>
        ) : emailMismatch ? (
          <>
            <div className="rounded-xl border border-warning/40 bg-warning/10 p-4 text-sm">
              You’re signed in as <strong>{user.email}</strong>, but this invitation was sent to <strong>{invite.email}</strong>.
            </div>
            <Button
              size="lg"
              variant="outline"
              loading={logout.isPending}
              onClick={() => logout.mutate(undefined, { onSettled: () => void navigate({ to: '/login', search: { redirect } }) })}
            >
              Sign in with a different account
            </Button>
          </>
        ) : (
          <Button
            size="lg"
            variant="brand"
            loading={accept.isPending || switchTenant.isPending}
            onClick={() =>
              accept.mutate(undefined, {
                onSuccess: async () => {
                  // The contract doesn't say whether accepting makes the tenant current — make sure it is.
                  const next = await qc.fetchQuery(meQueryOptions)
                  const joined = next?.memberships.find((m) => m.tenant.name === invite.tenant_name)
                  if (joined && next?.current_tenant?.id !== joined.tenant.id) {
                    await switchTenant.mutateAsync(joined.tenant.id).catch(() => undefined)
                  }
                  toast.success(`Welcome to ${invite.tenant_name}!`)
                  void navigate({ to: '/' })
                },
                onError: (e) => notifyError(e),
              })
            }
          >
            Accept invitation
          </Button>
        )}
      </div>
    </AuthLayout>
  )
}
